import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ArrowSquareOutIcon, CaretDownIcon, TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { InvestmentSourceList } from '@/components/InvestmentSources';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import Image from '@/components/ui/Image';
import { InvestmentAvailabilityNotice } from '@/components/InvestmentAvailabilityNotice';
import { InvestmentStatusBadge, InvestmentWithdrawnNotice } from '@/components/InvestmentStatusBadge';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useFinance } from '@/hooks/useFinance';
import {
  TRANSACTION_TYPE_LABELS,
  formatDate,
  formatPercent,
  getNonZeroExpenses,
  getTypeBadgeClass,
} from '@/lib/investments/transactions';
import { investmentsApi } from '@/lib/api';
import { useRouter } from '@/lib/router';
import { track } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import type { InvestmentDetail, InvestmentSource, InvestmentTransaction } from '@/types';

interface Props {
  /** Position to show; `null` keeps the drawer closed. */
  investmentId: string | null;
  /** Group this position belongs to, when several connections report it. */
  groupId?: string | null;
  /** Every connection folded into the row, passed from the list so the grouping renders
   * immediately. The detail response carries the same thing and supersedes it once loaded. */
  sources?: InvestmentSource[];
  onOpenChange: (open: boolean) => void;
  /** Called after a source is detached, so the page reloads totals. */
  onUnmerged?: () => void;
}

function formatDueMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('. de ', '/');
}

function StatCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface-sunken p-3">
      <p className="text-3xs font-semibold tracking-widest text-text-tertiary uppercase">{label}</p>
      <div className="mt-1 font-mono text-sm font-medium text-text-primary tabular-nums">{children}</div>
    </div>
  );
}

function TransactionRow({ tx }: { tx: InvestmentTransaction }) {
  // Hook in the leaf, not a threaded prop — keeps privacy-awareness of money
  // strings unforgeable by callers (same pattern as InvestmentAllocationChart).
  const formatCurrency = useFormatCurrency();
  const [expanded, setExpanded] = useState(false);

  function handleToggleExpanded() {
    setExpanded((current) => !current);
  }

  const facts: Array<[string, string]> = [];
  if (tx.quantity != null) facts.push(['Quantidade', tx.quantity.toLocaleString('pt-BR', { maximumFractionDigits: 6 })]);
  if (tx.unitPrice != null) facts.push(['Preço unit.', formatCurrency(tx.unitPrice)]);
  if (tx.netValue != null && tx.netValue !== tx.value) facts.push(['Valor líquido', formatCurrency(tx.netValue)]);
  if (tx.tradeDate) facts.push(['Liquidação', formatDate(tx.tradeDate)]);
  if (tx.brokerageNumber) facts.push(['Nota', tx.brokerageNumber]);
  if (tx.agreedRate != null) facts.push(['Taxa acordada', `${tx.agreedRate.toFixed(4)}%`]);
  const expenses = tx.expenses ? getNonZeroExpenses(tx.expenses) : [];
  const expensesTotal = expenses.reduce((sum, e) => sum + e.value, 0);
  const hasExtra = facts.length > 0 || expenses.length > 0 || Boolean(tx.description);

  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        type="button"
        disabled={!hasExtra}
        aria-expanded={hasExtra ? expanded : undefined}
        onClick={handleToggleExpanded}
        className={cn(
          'grid w-full grid-cols-[3.5rem_auto_1fr_1rem] items-center gap-x-2.5 px-5 py-3 text-left transition-colors',
          hasExtra ? 'cursor-pointer hover:bg-surface-sunken' : 'cursor-default',
        )}
      >
        <span className="font-mono text-sm text-text-secondary tabular-nums">{formatDate(tx.date).slice(0, 5)}</span>
        <span>
          <span className={cn('inline-flex rounded px-2 py-0.5 text-2xs font-bold uppercase', getTypeBadgeClass(tx.type))}>
            {TRANSACTION_TYPE_LABELS[tx.type] ?? tx.type}
          </span>
        </span>
        <span className="min-w-0 truncate text-right font-mono text-sm font-bold text-text-primary tabular-nums">
          {formatCurrency(tx.value)}
        </span>
        <span className="flex justify-end">
          {hasExtra && (
            <CaretDownIcon size={12} className={cn('text-text-tertiary transition-transform', expanded && 'rotate-180')} />
          )}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border-subtle bg-surface-sunken px-5 py-3">
          {tx.description && <p className="mb-2.5 text-sm text-text-secondary">{tx.description}</p>}
          {facts.length > 0 && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {facts.map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-3xs font-semibold tracking-widest text-text-tertiary uppercase">{label}</dt>
                  <dd className="mt-0.5 truncate font-mono text-sm text-text-primary tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          {expenses.length > 0 && (
            <div className={cn(facts.length > 0 && 'mt-3 border-t border-border-subtle pt-2.5')}>
              <p className="text-3xs font-semibold tracking-widest text-text-tertiary uppercase">Custos</p>
              <dl className="mt-1.5 space-y-1">
                {expenses.map((expense) => (
                  <div key={expense.label} className="flex items-center justify-between gap-3">
                    <dt className="text-xs text-text-secondary">{expense.label}</dt>
                    <dd className="font-mono text-xs text-text-primary tabular-nums">{formatCurrency(expense.value)}</dd>
                  </div>
                ))}
                {expenses.length > 1 && (
                  <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-1">
                    <dt className="text-xs font-bold text-text-secondary">Total</dt>
                    <dd className="font-mono text-xs font-bold text-text-primary tabular-nums">{formatCurrency(expensesTotal)}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UnmergeButton({
  source,
  groupId,
  onUnmerged,
}: {
  source: InvestmentSource;
  groupId: string;
  onUnmerged?: () => void;
}) {
  const { getToken } = useAuth();
  const [pending, setPending] = useState(false);

  if (source.role === 'PRIMARY') return null;

  async function handleClick() {
    setPending(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      // The membership's id; until core.api's VER-581 is live a source carries none and the
      // route takes the investment's id.
      await investmentsApi.unmergeInvestment(token, groupId, source.memberId ?? source.investmentId);
      track('action.investment.unmerge', { role: source.role === 'MIRROR' ? 'mirror' : 'additive' });
      // Closes the loop the old permanent unmerge left open: the pair goes back to the
      // review queue, so tell the user where to find it if they change their mind.
      toast.success('Posição separada. Para reagrupar, veja Itens para revisar.');
      onUnmerged?.();
    } catch {
      toast.error('Não foi possível separar esta posição.');
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      className="shrink-0 cursor-pointer rounded-lg px-1.5 py-0.5 text-3xs font-bold text-text-tertiary transition-colors hover:bg-accent hover:text-error-text disabled:opacity-50"
    >
      Separar
    </button>
  );
}

/** Connections reporting this holding. Self-guards: nothing to say about a position only
 * one connection reports. */
function InvestmentSourcesBlock({
  sources,
  groupId,
  onUnmerged,
}: {
  sources: InvestmentSource[];
  groupId: string | null;
  onUnmerged?: () => void;
}) {
  const renderAction = useCallback(
    (source: InvestmentSource) =>
      groupId ? <UnmergeButton source={source} groupId={groupId} onUnmerged={onUnmerged} /> : null,
    [groupId, onUnmerged],
  );

  if (sources.length < 2) return null;

  // @container, not viewport breakpoints — on desktop this drawer is a narrow right panel.
  return (
    <div className="@container border-b border-border-subtle px-5 py-4">
      <h3 className="mb-2 text-sm font-semibold text-text-primary">Instituições</h3>
      <InvestmentSourceList sources={sources} renderAction={renderAction} />
    </div>
  );
}

/**
 * Quick-look drawer for a portfolio position (right panel on desktop, bottom sheet on
 * mobile) — the list keeps its filters/scroll behind the scrim. The full
 * /investments/:id page stays reachable via the footer for deep dives.
 */
export function InvestmentDetailDrawer({
  investmentId,
  groupId = null,
  sources,
  onOpenChange,
  onUnmerged,
}: Props) {
  const router = useRouter();
  const formatCurrency = useFormatCurrency();
  const { fetchInvestmentDetail } = useFinance();

  // The loaded detail is keyed to the id it was fetched for, so switching positions
  // (or an open animation frame before the effect runs) can never show another
  // position's data or a premature failure message — both derive per render.
  const [loaded, setLoaded] = useState<{ id: string; detail: InvestmentDetail } | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);

  useEffect(() => {
    if (!investmentId) return;
    let alive = true;
    fetchInvestmentDetail(investmentId, { limit: 50, offset: 0 })
      .then((result) => {
        if (!alive) return;
        if (result) setLoaded({ id: investmentId, detail: result });
        else setFailedId(investmentId);
      })
      .catch(() => {
        if (alive) setFailedId(investmentId);
      });
    return () => {
      alive = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentId]);

  function handleUnmerged() {
    // Detaching leaves this position ungrouped, so its sources block would be empty — close the
    // drawer and let the pair speak for itself back in the review card underneath.
    onOpenChange(false);
    onUnmerged?.();
  }

  const data = investmentId && loaded?.id === investmentId ? loaded.detail : null;
  const loadFailed = investmentId !== null && failedId === investmentId;

  const inv = data?.investment;
  const transactions = data?.transactions.items ?? [];
  const total = data?.transactions.total ?? transactions.length;

  function handleOpenFullPage() {
    if (inv) router.push(`/investments/${inv.id}`);
  }

  return (
    <Drawer open={!!investmentId} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="flex flex-col bg-surface px-0 data-[vaul-drawer-direction=right]:h-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="px-5 pt-3 pb-0">
          <DrawerTitle className="sr-only">Detalhes do investimento</DrawerTitle>
          <DrawerDescription className="sr-only">
            Posição selecionada com saldo, rentabilidade e movimentações.
          </DrawerDescription>
        </DrawerHeader>

        {!inv ? (
          loadFailed ? (
            <p className="px-5 py-8 text-sm text-text-secondary">Não foi possível carregar esta posição.</p>
          ) : (
            <div className="space-y-3 px-5 py-4">
              <div className="h-4 w-32 animate-pulse rounded bg-surface-sunken" />
              <div className="h-6 w-2/3 animate-pulse rounded bg-surface-sunken" />
              <div className="h-24 animate-pulse rounded-xl bg-surface-sunken" />
            </div>
          )
        ) : (
          <>
            {/* Hero — institution, name, saldo, profit chip */}
            <div className="border-b border-border-subtle px-5 pt-1 pb-4">
              <div className="flex items-center gap-2">
                {inv.institutionImage && (
                  <Image src={inv.institutionImage} alt="" width={16} height={16} className="shrink-0 rounded" />
                )}
                <span className="min-w-0 truncate text-xs text-text-secondary">
                  {inv.institution || inv.issuer || '—'}
                </span>
              </div>
              <h2 className="mt-1 flex flex-wrap items-center gap-1.5 text-lg/tight font-bold text-text-primary">
                <span className="wrap-break-word">{inv.name}</span>
                <InvestmentStatusBadge status={inv.status} />
              </h2>
              <p className="mt-1 text-xs text-text-tertiary">
                {[inv.subtype, inv.issuer, inv.rate].filter(Boolean).join(' · ')}
              </p>

              <div className="mt-3 space-y-2 empty:hidden">
                <InvestmentWithdrawnNotice status={inv.status} />
                <InvestmentAvailabilityNotice inv={inv} />
              </div>

              <div className="mt-4 flex items-end justify-between gap-3">
                <p className="font-mono text-2xl font-semibold tracking-tight text-text-primary tabular-nums">
                  {formatCurrency(inv.balance)}
                </p>
                {inv.profitPercent != null && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full py-1 pr-2.5 pl-2 text-xs font-bold',
                      inv.profitPercent >= 0 ? 'bg-success-surface text-success-text' : 'bg-error-surface text-error-text',
                    )}
                  >
                    {inv.profitPercent >= 0 ? <TrendUpIcon size={12} /> : <TrendDownIcon size={12} />}
                    {formatPercent(inv.profitPercent)}
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-border-subtle">
                <StatCell label="Investido">
                  {inv.amountOriginal != null ? formatCurrency(inv.amountOriginal) : '—'}
                </StatCell>
                <StatCell label="Lucro">
                  {inv.profit != null ? (
                    <span className={inv.profit >= 0 ? 'text-amount-positive' : 'text-amount-negative'}>
                      {inv.profit >= 0 ? '+' : ''}
                      {formatCurrency(inv.profit)}
                    </span>
                  ) : (
                    '—'
                  )}
                </StatCell>
                <StatCell label="Rentab. anual">
                  {inv.annualRate != null ? `${inv.annualRate.toFixed(2)}%` : inv.rate || '—'}
                </StatCell>
                <StatCell label="Vencimento">
                  {inv.dueDate ? formatDueMonth(inv.dueDate) : '—'}
                </StatCell>
              </div>
            </div>

            <InvestmentSourcesBlock
              sources={inv.sources ?? sources ?? []}
              groupId={inv.groupId ?? groupId}
              onUnmerged={handleUnmerged}
            />

            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-text-primary">
                Movimentações{' '}
                <span className="font-normal text-text-tertiary">
                  ({transactions.length}
                  {total > transactions.length ? ` de ${total}` : ''})
                </span>
              </h3>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {transactions.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-text-secondary">Nenhuma movimentação encontrada</p>
              ) : (
                transactions.map((tx) => <TransactionRow key={tx.id} tx={tx} />)
              )}
            </div>

            <div className="border-t border-border-subtle p-3">
              <button
                type="button"
                onClick={handleOpenFullPage}
                className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-surface-sunken text-sm font-bold text-text-primary transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-800"
              >
                Abrir página completa
                <ArrowSquareOutIcon size={14} />
              </button>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
